/**
 * Malteurop Malting Company — North American houses in Winona (Minnesota),
 * Winnipeg and Great Falls (Montana), plus imported lots from the European
 * plants.
 *
 * Colour is published in °L (and SRM; for malt colour those are the same
 * ASBC measurement). `colorLovibond` is the mid-point of the published
 * Lovibond band and EBC is derived with `EBC = 2.65 × °L − 1.2`. Moisture is
 * the mid-point of a published band, or the published maximum where they
 * print only a ceiling. Diastatic power is stored as °Lintner: the North
 * American typical-analysis tables label the column "Diastatic Power (EBC)"
 * but the figures (140–175 on 2-row, 80–140 on the imported pilsens) are
 * Lintner-scale, and the French Pilsen sell sheet prints the same range as
 * `ºL`.
 *
 * Extract is the gap. Only the French Pilsen sell sheet publishes a
 * fine-grind dry-basis figure; that row is `spec-sheet`. Every other SKU
 * carries a class-typical extract and is `representative`, even where colour,
 * moisture and diastatic power are the maker's.
 *
 * @see https://www.malteuropmaltingco.com/en/
 */

import type { Fermentable } from '../../schema/fermentable';

/** Malteurop malts. @see Fermentable */
export const malteuropFermentables: readonly Fermentable[] = [
  {
    id: 'malteurop-2-row',
    name: '2-Row',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 2.2,
    colorEbc: 4.6,
    diastaticPowerLintner: 163,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The flagship North American two-row: sweet, slightly nutty and pale enough for anything. High enzyme, so it converts adjuncts as comfortably as it converts itself.',
    tags: ['base', 'two-row', 'american', 'high-enzyme'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/2-row',
    provenance: 'representative',
  },
  {
    id: 'malteurop-pilsen',
    name: 'Pilsen',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.4,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 148,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of their North American base malts. Straw-yellow wort with a quiet white-bread sweetness, and enough enzyme to be the whole grist for a pilsner or a hop-forward pale.',
    tags: ['base', 'lager', 'pilsner', 'american'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/pilsen',
    provenance: 'representative',
  },
  {
    id: 'malteurop-pale-ale',
    name: 'Pale Ale',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.4,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned warmer than the 2-row, with a light biscuit and nut note and a gold wort. The ale base when the beer wants more malt than pilsen gives, without going as far as Vienna.',
    tags: ['base', 'ale', 'pale-ale', 'american'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/pale-ale',
    provenance: 'representative',
  },
  {
    id: 'malteurop-vienna',
    name: 'Vienna',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.4,
    colorLovibond: 3.2,
    colorEbc: 7.3,
    diastaticPowerLintner: 168,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Lightly kilned Vienna with a biscuit maltiness and a rich gold. Unusual enzyme for the class, so it can be the whole grist for an amber lager or a malt-forward ale.',
    tags: ['base', 'kilned', 'vienna', 'american'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/vienna',
    provenance: 'representative',
  },
  {
    id: 'malteurop-munich-5l',
    name: 'Munich 5L',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 5,
    colorEbc: 12.1,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light Munich, bready and nutty without the depth of the darker grade. Built to be a large share of a festbier, pale ale or IPA grist rather than a 5 % seasoning.',
    tags: ['kilned', 'munich', 'american', 'light'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/munich-5l',
    provenance: 'representative',
  },
  {
    id: 'malteurop-munich',
    name: 'Munich',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker Munich: clean, intense malt and a deep-gold to amber wort. Enough enzyme to be a primary base, though the maker caps it at half the grist so the color does not run away.',
    tags: ['kilned', 'munich', 'american'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/munich',
    provenance: 'representative',
  },
  {
    id: 'malteurop-wheat',
    name: 'Wheat Malt',
    maker: 'Malteurop',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 5,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Soft white wheat, malted. Doughy, bread-sweet and high in enzyme; a weissbier or wit base, or 5–15 % in anything that wants foam and a fuller mouthfeel.',
    tags: ['base', 'wheat', 'american', 'head-retention'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/north-american-malts/wheat',
    provenance: 'representative',
  },
  {
    id: 'malteurop-french-pilsen',
    name: 'French Pilsen',
    maker: 'Malteurop',
    origin: 'France',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row spring barley grown in Champagne and malted at the nearby French houses. Mild, malty and pale — a continental pilsner base rather than a North American high-enzyme one.',
    tags: ['base', 'lager', 'pilsner', 'french'],
    url: 'https://www.malteuropmaltingco.com/wp-content/uploads/2025/07/malteurop_french_pilsen_sellsheet_digital.pdf',
    provenance: 'spec-sheet',
  },
  {
    id: 'malteurop-bavarian-pilsen',
    name: 'Bavarian Pilsen',
    maker: 'Malteurop',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Bavarian two-row spring barley, malted in Bavaria. Mild, malty and straw-pale — the imported German pilsner base for heritage lager recipes.',
    tags: ['base', 'lager', 'pilsner', 'german'],
    url: 'https://www.malteuropmaltingco.com/en/mmcmalt/import-malts/bavarian-pilsen',
    provenance: 'representative',
  },
];
